import LanguageSelector from '../i18n/LanguageSelector';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { SPORTS } from '../data/sports';
import SportUniverse from '../components/SportUniverse';
import AuthLogo from '../components/AuthLogo';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Container, TextField, Button, Typography, Box, Alert, Paper, FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { usePageMeta } from '../utils/usePageMeta';
import { COUNTRIES } from '../data/countries';



const Register = () => {
  const { tr, locale } = useAppTranslation();
  usePageMeta('Inscription — G1Sport', 'Créez votre compte G1Sport gratuitement. Sportifs : accès gratuit. Coachs : 6 mois offerts.');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Initialize role from location state if available, otherwise default to 'SPORTIF'
  const [role, setRole] = useState((location.state as any)?.role || 'SPORTIF');
  const [sport, setSport] = useState(SPORTS.includes(location.state?.sport) ? location.state.sport : '');
  const [country, setCountry] = useState('');
  const [department, setDepartment] = useState('');
  const [city, setCity] = useState('');
  const [countriesList, setCountriesList] = useState<any[]>([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Liste des pays embarquee en local (plus d'appel a une API externe qui
    // laissait le menu vide). France selectionnee par defaut.
    setCountriesList(COUNTRIES);
    setCountry('France');
  }, []);

  useEffect(() => {
    if (country === 'France') {
      // Fetch departments for France
      fetch('https://geo.api.gouv.fr/departements')
        .then(res => res.json())
        .then(data => setDepartmentsList(data))
        .catch(console.error);
    } else {
      setDepartmentsList([]);
      setCitiesList([]);
    }
  }, [country]);

  useEffect(() => {
    if (department && country === 'France') {
      // Fetch cities for the selected department
      fetch(`https://geo.api.gouv.fr/departements/${department}/communes`)
        .then(res => res.json())
        .then(data => setCitiesList(data))
        .catch(console.error);
    }
  }, [department, country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const payload: any = {
      username,
      email,
      password,
      role,
      country,
      department: (country === 'France' ? departmentsList.find(d => d.code === department)?.nom : department) || '',
      city: (country === 'France' ? citiesList.find(c => c.code === city)?.nom : city) || ''
    };

    if (sport) {
      if (role === 'COACH') {
        payload.coach_profile = { sport };
      } else {
        payload.sportif_profile = { sport };
      }
    }

    try {
      await api.post('/users/register/', payload);
      setSuccess(true);
    } catch (err: any) {
      // Remonter le vrai motif d'echec (ex. « nom d'utilisateur deja pris »,
      // « e-mail deja utilise ») au lieu d'un message generique.
      const data = err?.response?.data;
      let msg = "L'inscription a échoué. Veuillez réessayer.";
      if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
        msg = "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.";
      } else if (data && typeof data === 'object') {
        const first = Object.entries(data)[0];
        if (first) {
          const [field, val] = first;
          const detail = Array.isArray(val) ? val[0] : val;
          const labels: Record<string, string> = {
            username: "Nom d'utilisateur", email: 'E-mail', password: 'Mot de passe',
          };
          msg = `${labels[field] || field} : ${detail}`;
        }
      } else if (typeof data === 'string') {
        msg = data;
      }
      setError(msg);
      console.error(err);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', position: 'relative', px: isMobile ? 2 : 3, py: isMobile ? 2 : 0 }}>
      <Button 
        component={Link} 
        to="/" 
        startIcon={<ArrowBack />} 
        variant="outlined"
        size={isMobile ? "small" : "medium"}
        sx={{
          position: 'absolute',
          // Sous la zone sure (encoche/camera) pour rester cliquable sur mobile.
          top: isMobile ? 'calc(16px + env(safe-area-inset-top))' : 24,
          left: isMobile ? 16 : 24,
          fontWeight: 700,
          zIndex: 1200,
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'white',
          color: 'text.primary',
          borderColor: 'divider',
          backdropFilter: 'blur(20px)',
          boxShadow: (theme) => theme.palette.mode === 'light' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
          '&:hover': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.9)' : '#f8fafc',
            borderColor: 'primary.main',
            transform: 'translateY(-2px)'
          }
        }}
      >
         {tr("Retour")} </Button>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: isMobile ? 'calc(72px + env(safe-area-inset-top))' : 0 }}>
        <Paper sx={{ 
          p: isMobile ? 3 : 4, 
          width: '100%', 
          borderRadius: 4,
          backdropFilter: 'blur(20px)',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.05)',
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.05)'
        }}>
<LanguageSelector/>

          <Box sx={{ textAlign: 'center', mb: 1, width: '100%' }}>
            <AuthLogo />
            <Typography variant={isMobile ? "h4" : "h4"} sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 0, color: '#FFD700' }}>
              G1Sport
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 0 }}>
               {tr("Rejoins une communauté de coaching moderne.")} </Typography>
          </Box>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
             {tr("Inscription")} </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
             {tr("Choisissez votre profil Sportif ou Coach pour continuer.")} </Typography>

          {location.state?.coach && <Alert severity="info" sx={{ mt: 2 }}>{tr("Coach sélectionné :")} {location.state.coach.username}{tr(". Retrouvez-le après connexion dans « Trouver un Coach ».")}</Alert>}
          {location.state?.intent && <Alert severity="info" sx={{ mt: 2 }}>{tr("Créez votre compte pour")} {location.state.intent === 'booking' ? tr("réserver une séance auprès d’un coach") : tr("créer une séance dans votre espace coach")}{tr(". Le profil Coach permet de créer et gérer les séances ; le profil Sportif permet de réserver.")}</Alert>}
          <Box sx={{ mt: 2 }}><SportUniverse sport={sport} compact /></Box>
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>{tr("Compte créé avec succès !")}</Typography>
               {tr("Un lien de confirmation a été envoyé à")} <strong>{email}</strong>{tr(". Ouvrez-le pour activer votre compte avant de vous connecter. Pensez à vérifier les courriers indésirables.")} <Box sx={{ mt: 1.5 }}>
                <Button variant="contained" size="small" onClick={() => navigate('/login', { state: location.state })}>{tr("Se connecter")}</Button>
                <Button component={Link} to="/confirmer-email" size="small">{tr("Renvoyer le lien")}</Button>
              </Box>
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%', display: success ? 'none' : 'block' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? tr(error) : error}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={tr("Nom d'utilisateur")}
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={tr("Email")}
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={tr("Mot de passe")}
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="country-label">{tr("Pays")}</InputLabel>
            <Select
              labelId="country-label"
              id="country"
              value={countriesList.some(c => (c.translations?.fra?.common || c.name.common) === country) ? country : ''}
              label={tr("Pays")}
              onChange={(e) => {
                setCountry(e.target.value as string);
                setDepartment('');
                setCity('');
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {countriesList.map((c) => {
                const name = c.translations?.fra?.common || c.name.common;
                return (
                  <MenuItem key={c.cca2} value={name}>
                    {new Intl.DisplayNames([locale], { type: 'region' }).of(c.cca2) || name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {country === 'France' ? (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel id="department-label">{tr("Département")}</InputLabel>
                <Select
                  labelId="department-label"
                  id="department"
                  value={departmentsList.some(d => d.code === department) ? department : ''}
                  label={tr("Département")}
                  onChange={(e) => {
                    setDepartment(e.target.value as string);
                    setCity('');
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {departmentsList.map((d) => (
                    <MenuItem key={d.code} value={d.code}>
                      {d.nom} ({d.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" disabled={!department}>
                <InputLabel id="city-label">{tr("Ville")}</InputLabel>
                <Select
                  labelId="city-label"
                  id="city"
                  value={citiesList.some(c => c.code === city) ? city : ''}
                  label={tr("Ville")}
                  onChange={(e) => setCity(e.target.value as string)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {citiesList.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.nom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <>
               <TextField
                margin="normal"
                fullWidth
                id="department"
                label={tr("État / Province / Département")}
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                id="city"
                label={tr("Ville")}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel id="role-label">{tr("Profil")}</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              value={role}
              label={tr("Profil")}
              onChange={(e) => setRole(e.target.value as string)}
            >
              <MenuItem value="SPORTIF">{tr("Sportif")}</MenuItem>
              <MenuItem value="COACH">{tr("Coach")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="sport-label">
              {role === 'COACH' ? tr("Sport enseigné") : tr("Sport pratiqué")}
            </InputLabel>
            <Select
              labelId="sport-label"
              id="sport"
              value={sport}
              label={role === 'COACH' ? tr("Sport enseigné") : tr("Sport pratiqué")}
              onChange={(e) => setSport(e.target.value as string)}
            >
              {SPORTS.map((s) => (
                <MenuItem key={s} value={s}>{tr(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
             {tr("Créer mon compte")} </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Link to="/login" state={location.state} style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                 {tr("Déjà un compte ? Se connecter")} </Typography>
            </Link>
          </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
